import { authedFetch } from "./client";
import type {
  AppActionResponse,
  PreviewWakeResponse,
  TaskApp,
} from "@bash/shared";

/**
 * Wake a dormant app preview — the "Go live" button's call.
 *
 * The server resumes the scaled-to-zero sandbox, and — since a resumed dev server
 * can come back wedged — may kill and relaunch the app before answering. That
 * makes this call slow by design (up to ~10s of restart polling), so it carries
 * no client timeout: abandoning it early would give up on a wake still in flight.
 *
 * Business outcomes ride a 200 with ok:false, so only a transport or auth failure
 * throws (authedFetch rejects on any non-2xx). A rejection maps to
 * preview_unavailable — "try again in a moment" — rather than server_gone,
 * because a network blip tells us nothing about whether the app is still there.
 */
export async function wakeLivePreview(
  taskId: string
): Promise<PreviewWakeResponse> {
  try {
    const res = await authedFetch(`/api/tasks/${taskId}/preview/wake`, {
      method: "POST",
    });
    return (await res.json()) as PreviewWakeResponse;
  } catch {
    return { ok: false, error: "preview_unavailable" };
  }
}

/**
 * The user's apps across tasks (one row per pool port), from the durable
 * task_apps rows. The socket's apps:changed carries the same list on every
 * change; this call seeds the store after a reload and after a reconnect.
 *
 * Resolves `null` when the truth is unknown (transport failure, or a body
 * that is not the contract): an empty list is a real answer, "could not ask"
 * is not, and the store must never mistake one for the other.
 */
export async function listApps(): Promise<TaskApp[] | null> {
  try {
    const res = await authedFetch("/api/apps");
    const body = (await res.json()) as { apps?: unknown };
    return Array.isArray(body?.apps) ? (body.apps as TaskApp[]) : null;
  } catch {
    return null;
  }
}

/** Shared shape of the three per-port actions; only the verb differs. */
async function appAction(
  port: number,
  verb: "wake" | "start"
): Promise<AppActionResponse> {
  try {
    const res = await authedFetch(`/api/apps/${port}/${verb}`, {
      method: "POST",
    });
    return (await res.json()) as AppActionResponse;
  } catch {
    return { ok: false, error: "preview_unavailable" };
  }
}

/** Wake an app by port: probe, relaunch if wedged, re-mint its link. Slow by
 *  design (same reasons as wakeLivePreview), so no client timeout. */
export function wakeApp(port: number): Promise<AppActionResponse> {
  return appAction(port, "wake");
}

/** Start a stopped app by port: relaunch from its captured command and bring
 *  the same link back. */
export function startApp(port: number): Promise<AppActionResponse> {
  return appAction(port, "start");
}

/** Stop an app by port: the process is killed and its link closed. The list
 *  update rides apps:changed; callers do not patch the store themselves. */
export async function stopApp(
  port: number
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await authedFetch(`/api/apps/${port}/stop`, { method: "POST" });
    return (await res.json()) as { ok: true } | { ok: false; error: string };
  } catch {
    return { ok: false, error: "preview_unavailable" };
  }
}

/**
 * An app's face as a 60 s pass, minted for its owner. An <img> cannot carry the
 * bearer, so the pass is fetched as JSON and handed to the img. Null on any failure
 * (the window shows its blank letter, as it does before a face exists).
 */
export async function readAppFace(thumbUrl: string): Promise<string | null> {
  try {
    const res = await authedFetch(thumbUrl, {
      headers: { Accept: "application/json" },
    });
    const body = (await res.json()) as { url?: unknown };
    return typeof body?.url === "string" ? body.url : null;
  } catch {
    return null;
  }
}
